import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, Bell, LogOut, User, ChevronDown } from "lucide-react";
import { clearAccessToken, getUser } from "utills/appStorage";
import useNotifications from "hooks/useNotifications";
import notificationRoute from "utills/notificationRoute";
import { INotification } from "types/notification";
import { pageTitleFor } from "./navItems";

interface Props {
  onMenuClick: () => void;
}

/**
 * The bell, wired to somewhere.
 *
 * This used to be a static button with a hardcoded red dot — it opened nothing
 * and the dot was on whether or not anything had happened. The routing work in
 * `utills/notificationRoute` was written against `Common/NotificationDropdown`,
 * which is only mounted by the dead `Layout/Header.tsx`, so none of it reached
 * an operator.
 *
 * Built here rather than reusing that component: it is styled for the old
 * Tailwick topbar with `group-data-[topbar=…]` variants that never match any
 * more, and its `min-w-[20rem]` panel is wider than a 375px screen once it is
 * anchored to the right edge.
 */
const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useNotifications(6);

  const notifications = data ?? [];
  const unread = notifications.filter((item) => !item.read).length;

  const go = (notification: INotification) => {
    setOpen(false);
    navigate(notificationRoute(notification) ?? "/notifications");
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        data-touch="icon"
        className="relative text-gray-500 hover:text-gray-800 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          {/* Anchored right and capped to the viewport, so it cannot hang off
              the edge of a phone the way the old 20rem panel did. */}
          <div className="absolute right-0 top-full mt-1 w-[min(20rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-100 z-20">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700">Notifications</p>
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-indigo-600 hover:underline"
              >
                See all
              </Link>
            </div>

            {notifications.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-gray-400">
                Nothing new
              </p>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification._id}
                  onClick={() => go(notification)}
                  className="w-full text-left px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
                >
                  <p
                    className={`text-xs ${
                      notification.read
                        ? "text-gray-600"
                        : "font-semibold text-gray-900"
                    }`}
                  >
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-400 line-clamp-2">
                    {notification.message}
                  </p>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default function AppHeader({ onMenuClick }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const user = getUser();

  // Derived from the same nav model the sidebar uses. The hand-kept map this
  // replaces covered 17 of 30 routes, so complaints, csr, verification orders
  // and eleven others all read "Mynda Admin" — invisible on desktop, but on a
  // phone this line is the only thing saying where you are.
  const pageName = pageTitleFor(pathname);

  const handleLogout = () => {
    clearAccessToken();
    navigate("/login");
  };

  return (
    <header className="fixed top-0 left-0 right-0 md:left-64 h-16 bg-white border-b border-gray-200 z-10 flex items-center px-4 gap-4">
      <button
        onClick={onMenuClick}
        aria-label="Open navigation"
        data-touch="icon"
        className="md:hidden text-gray-500 hover:text-gray-800 p-1 rounded"
      >
        <Menu size={20} />
      </button>

      <div className="flex-1 min-w-0">
        <h1 className="text-base font-semibold text-gray-800 truncate">
          {pageName}
        </h1>
        <p className="text-xs text-gray-400 hidden sm:block">
          Mynda Operations &rsaquo; {pageName}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <NotificationBell />

        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700">
              <User size={14} />
            </div>
            <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
              {user?.firstName ?? "Admin"}
            </span>
            <ChevronDown size={14} className="text-gray-400" />
          </button>

          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-20 py-1">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
